import { removeImpersonateToken, type ToastOptionsModel, useGetQuery, usePutMutation } from 'shared/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { getApiType, isImpersonate } from 'shared/lib/api';
import { pageRoutes } from 'shared/routes';
import type { Holiday, HolidaysToSubmit } from 'widgets/HolidaySchedule';

import type {
  NotificationsData,
  AuthenticatedContractorModel,
  AuthenticatedManagerModel,
  UpdatePasswordModel,
  UpdateProfileModel,
  UpdateAcceptTermsModel,
  UpdateCampaignModel,
} from '../index';

import { profileRoutes } from './routes';

const userQueryKey = ['getUser'];

export const useGetNotifications = ({ enabled }: { enabled?: boolean }) =>
  useGetQuery<NotificationsData>(profileRoutes.app.getNotifications, { enabled });
export const useUpdateIntegration = () => usePutMutation<NotificationsData>(profileRoutes.app.updateNotifications);

export function useUpdateContractor(toastOptions?: ToastOptionsModel) {
  const queryClient = useQueryClient();

  return usePutMutation<UpdateProfileModel | UpdatePasswordModel | UpdateAcceptTermsModel | UpdateCampaignModel>(
    profileRoutes.app.updateUser,
    {
      toastOptions,
      onSuccess: () => {
        void queryClient.invalidateQueries(userQueryKey);
      },
    }
  );
}

export function useGetUser<T>() {
  const history = useHistory();
  const context = useGetQuery<T>(profileRoutes[getApiType()].getUser, {
    queryKey: userQueryKey,
    enabled: true,
  });

  if (context.error?.response.status === 401 && isImpersonate()) {
    removeImpersonateToken();
    history.push(pageRoutes.app.impersonateError);
  }

  return context;
}

export function useGetManager() {
  return useGetUser<AuthenticatedManagerModel>();
}

export function useGetContractor() {
  return useGetUser<AuthenticatedContractorModel>();
}

export const useHolidaySchedule = () => useGetQuery<{ holidays: Holiday[] }>(profileRoutes.app.getHolidaySchedule);

export function useUpdateHolidaySchedule(toastOptions?: ToastOptionsModel) {
  const queryClient = useQueryClient();

  return usePutMutation<HolidaysToSubmit>(profileRoutes.app.updateHolidaySchedule, {
    toastOptions,
    onSuccess: () => {
      void queryClient.invalidateQueries(userQueryKey);
    },
  });
}
